import type {
  ActionRecord,
  CollaborationWorkspaceView,
  CommentRecord,
  DecisionRecord,
  FavouriteRow,
  GlobalFilters,
  NotificationRow,
  OnDemandReportRow,
  PolicyVersionRow,
  QueryResult,
  ReportRunRow,
  ReportsAnalyticsView,
  ScheduledReportRow,
  SubscriptionRow,
  TaxonomyChangeRow,
  TaxonomyGovernanceView,
  TaxonomyVersionRow,
  TaxonomyVocabularyRow,
  WatchlistGroup,
  WatchlistView,
} from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import type { Application, Person, Product } from '@aurora-pvi/seed';
import { SOURCE_SYSTEMS } from '@aurora-pvi/seed';
import { resolved, type RepositoryContext } from './context';
import { applyFilters } from './filters';
import { buildMetadata, chartPayload, metricValue } from './metadata';
import type { SeededPersona } from './types';

/**
 * Governance collaboration, watching and reporting (D13).
 *
 * Four projections behind one repository because they are four faces of the
 * same governance record: a comment raises a point, an action carries the work
 * it created, a decision closes it, a report tells someone outside the room
 * what happened, and a taxonomy version is what the whole conversation was
 * ultimately arguing about.
 *
 * Two properties are held here rather than in the screens, because a screen can
 * only honour them if the payload makes them possible.
 *
 * Nothing is marked read by being fetched. `isRead` is what the store holds;
 * the fixture never flips it on the way out, so a page that shows a badge of
 * three unread items shows three until the reader acts.
 *
 * A report's output date and its run date are separate fields. A run that
 * finished this morning against a feed that last landed on Friday describes
 * Friday, and `dataAsOf` is what says so — `isStale` is derived from that gap,
 * never from the run's own clock.
 */

// ── Dates ────────────────────────────────────────────────────────────────────

/** `asOf` shifted by whole days. Negative goes back. Dates only, no time. */
function shiftDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** A timestamp `days` before `asOf` at a stated UTC time. */
function momentBefore(asOf: string, days: number, time = '09:15'): string {
  return `${shiftDays(asOf, -days)}T${time}:00Z`;
}

function momentAfter(asOf: string, days: number, time = '06:00'): string {
  return `${shiftDays(asOf, days)}T${time}:00Z`;
}

function daysApart(from: string, to: string): number {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  return Math.round((b - a) / 86_400_000);
}

// ── Refs ─────────────────────────────────────────────────────────────────────

function productRef(product: Product) {
  return {
    id: product.id,
    entityType: 'Product',
    businessCode: product.businessCode,
    name: product.name,
    route: `/products/${product.id}`,
  };
}

function applicationRef(application: Application) {
  return {
    id: application.id,
    entityType: 'Application',
    businessCode: application.businessCode,
    name: application.name,
    route: `/applications/${application.id}`,
  };
}

/**
 * An owner, with the workforce field policy applied.
 *
 * Email is nulled rather than blanked for callers without workforce rights: a
 * blank string reads as "this person has no email", which is a different and
 * false claim.
 */
function ownerRef(person: Person, user: SeededPersona) {
  return {
    id: person.id,
    displayName: person.displayName,
    jobTitle: person.jobTitle,
    email: user.canSeeWorkforce ? person.email : null,
  };
}

export function createCollaborationRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;
  const { asOf } = dataset;

  // Stable pickers. Indexing into the seeded arrays keeps every code, name and
  // owner on these screens the same one the portfolio screens show, which is
  // the whole point of drawing them from the dataset rather than inventing
  // plausible strings.
  const product = (n: number): Product => {
    const found = dataset.products[n % dataset.products.length];
    if (!found) throw new Error('The seeded dataset holds no products.');
    return found;
  };
  const application = (n: number): Application => {
    const found = dataset.applications[n % dataset.applications.length];
    if (!found) throw new Error('The seeded dataset holds no applications.');
    return found;
  };
  const person = (n: number): Person => {
    const found = dataset.people[n % dataset.people.length];
    if (!found) throw new Error('The seeded dataset holds no people.');
    return found;
  };
  const personName = (n: number): string => person(n).displayName;

  /** Governance conversations touch commercial terms; those threads are gated. */
  const canReadRestrictedThreads = user.canSeeRestrictedFinance;

  const sources = [SOURCE_SYSTEMS.dataHub, SOURCE_SYSTEMS.productRegistry];

  // ── Comments ───────────────────────────────────────────────────────────────

  function buildComments(): CommentRecord[] {
    const seeds: Array<{
      code: string;
      subject: { ref: ReturnType<typeof productRef> | null; missingReason: string | null };
      authorIndex: number;
      days: number;
      time: string;
      body: string;
      visibility: CommentRecord['visibility'];
      replies: number;
      mentions: number[];
      resolvedDays?: number;
      resolvedByIndex?: number;
      action?: string;
      decision?: string;
    }> = [
      {
        code: 'CMT-2026-0411',
        subject: { ref: productRef(product(3)), missingReason: null },
        authorIndex: 5,
        days: 2,
        time: '08:12',
        body: 'The run cost on this product still excludes the two regional carriers we onboarded in March. Until Finance reallocates them the TCO here understates by roughly a fifth, so please do not quote it in the board pack.',
        visibility: 'Governance',
        replies: 4,
        mentions: [11, 18],
        action: 'ACT-2026-0188',
      },
      {
        code: 'CMT-2026-0409',
        subject: { ref: productRef(product(11)), missingReason: null },
        authorIndex: 12,
        days: 3,
        time: '14:47',
        body: 'Crew rostering and crew pairing are being counted as one product in the hierarchy and as two in the CMDB. Whichever is right, the application mapping is currently double counting six applications.',
        visibility: 'Team',
        replies: 7,
        mentions: [4],
        action: 'ACT-2026-0186',
      },
      {
        code: 'CMT-2026-0404',
        subject: { ref: productRef(product(27)), missingReason: null },
        authorIndex: 21,
        days: 5,
        time: '11:03',
        body: 'Supplier renegotiation terms discussed in the sourcing review. Commercially sensitive until the contract is signed.',
        visibility: 'Restricted',
        replies: 3,
        mentions: [7],
      },
      {
        code: 'CMT-2026-0398',
        subject: { ref: productRef(product(44)), missingReason: null },
        authorIndex: 9,
        days: 8,
        time: '16:30',
        body: 'Health dropped two bands in one refresh. That is the incident backlog landing all at once rather than a genuine deterioration — the scoring window needs widening before anyone reads a trend into it.',
        visibility: 'Team',
        replies: 5,
        mentions: [2, 15],
        resolvedDays: 4,
        resolvedByIndex: 2,
      },
      {
        code: 'CMT-2026-0393',
        subject: {
          ref: null,
          missingReason:
            'The application this thread was raised on was retired on 12 April 2026. The conversation is kept because the decision it produced is still in force.',
        },
        authorIndex: 16,
        days: 11,
        time: '09:41',
        body: 'Decommissioning this leaves two downstream feeds without a source. Someone needs to own the replacement before the cut-over date, not after it.',
        visibility: 'Governance',
        replies: 6,
        mentions: [16, 3],
        decision: 'DEC-2026-0071',
      },
      {
        code: 'CMT-2026-0388',
        subject: { ref: productRef(product(6)), missingReason: null },
        authorIndex: 3,
        days: 14,
        time: '13:22',
        body: 'The benefit figure quoted in the assessment is the annualised one; the funding line is quarterly. They are not comparable and the ratio in the summary is meaningless as written.',
        visibility: 'Governance',
        replies: 2,
        mentions: [9],
        resolvedDays: 9,
        resolvedByIndex: 9,
        action: 'ACT-2026-0179',
      },
      {
        code: 'CMT-2026-0381',
        subject: { ref: productRef(product(19)), missingReason: null },
        authorIndex: 24,
        days: 18,
        time: '10:05',
        body: 'Network planning has asked whether the slot-management capability belongs to this product or to the schedule builder. It affects who is accountable for the coming season, so it is worth deciding rather than leaving it ambiguous.',
        visibility: 'Team',
        replies: 9,
        mentions: [1, 22],
        decision: 'DEC-2026-0068',
      },
      {
        code: 'CMT-2026-0376',
        subject: { ref: productRef(product(33)), missingReason: null },
        authorIndex: 14,
        days: 23,
        time: '15:58',
        body: 'Contract renewal pricing and the exit clause. Restricted to the sourcing group and the product owner.',
        visibility: 'Restricted',
        replies: 4,
        mentions: [14],
      },
      {
        code: 'CMT-2026-0370',
        subject: { ref: productRef(product(52)), missingReason: null },
        authorIndex: 7,
        days: 29,
        time: '07:44',
        body: 'Confirming the ground handling integration is in scope for the value stream review. No action needed, recording it so the scope is not re-litigated in the meeting.',
        visibility: 'Team',
        replies: 1,
        mentions: [],
        resolvedDays: 26,
        resolvedByIndex: 7,
      },
      {
        code: 'CMT-2026-0364',
        subject: { ref: productRef(product(8)), missingReason: null },
        authorIndex: 19,
        days: 34,
        time: '12:19',
        body: 'Two sources disagree about the lifecycle stage. The registry says Sunset, the CMDB says Active. The steward queue has it, but the disagreement should be visible on the product page while it is open.',
        visibility: 'Governance',
        replies: 8,
        mentions: [5, 11],
        action: 'ACT-2026-0171',
      },
    ];

    return seeds.map((seed, position) => {
      const withheld = seed.visibility === 'Restricted' && !canReadRestrictedThreads;
      return {
        id: `${dataset.enterprise.id.slice(0, 24)}${(4000 + position).toString().padStart(12, '0')}`,
        businessCode: seed.code,
        subject: seed.subject,
        author: ownerRef(person(seed.authorIndex), user),
        postedAt: momentBefore(asOf, seed.days, seed.time),
        body: withheld ? null : seed.body,
        withheldReason: withheld
          ? 'This thread discusses commercial terms and is restricted to the sourcing group. The thread is listed so you can see a conversation exists on a record you govern.'
          : null,
        visibility: seed.visibility,
        replyCount: seed.replies,
        mentions: seed.mentions.map((n) => personName(n)),
        resolvedAt:
          seed.resolvedDays === undefined ? null : momentBefore(asOf, seed.resolvedDays, '17:00'),
        resolvedBy: seed.resolvedByIndex === undefined ? null : personName(seed.resolvedByIndex),
        raisedActionCode: seed.action ?? null,
        raisedDecisionCode: seed.decision ?? null,
      };
    });
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  function buildActions(): ActionRecord[] {
    const seeds: Array<
      Omit<ActionRecord, 'id' | 'owner' | 'subject'> & {
        ownerIndex: number | null;
        subjectProduct: number | null;
        subjectApplication: number | null;
        subjectMissing: string | null;
      }
    > = [
      {
        businessCode: 'ACT-2026-0188',
        title: 'Reallocate the two regional carrier cost centres to the product',
        ownerIndex: 11,
        subjectProduct: 3,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(5),
        raisedOn: shiftDays(asOf, -2),
        dueOn: shiftDays(asOf, 6),
        noDueDateReason: null,
        status: 'InProgress',
        blockedReason: null,
        closedOn: null,
        severity: 'Warning',
        originCommentCode: 'CMT-2026-0411',
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0186',
        title: 'Reconcile the crew product boundary between the registry and the CMDB',
        ownerIndex: 4,
        subjectProduct: 11,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(12),
        raisedOn: shiftDays(asOf, -3),
        dueOn: shiftDays(asOf, -1),
        noDueDateReason: null,
        status: 'Open',
        blockedReason: null,
        closedOn: null,
        severity: 'Error',
        originCommentCode: 'CMT-2026-0409',
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0184',
        title: 'Name a replacement source for the two downstream feeds before cut-over',
        ownerIndex: null,
        subjectProduct: null,
        subjectApplication: 41,
        subjectMissing: null,
        unownedReason:
          'The owning team was disbanded in the March reorganisation and no successor has been recorded. Reassignment sits with the value stream lead.',
        raisedBy: personName(16),
        raisedOn: shiftDays(asOf, -11),
        dueOn: shiftDays(asOf, 12),
        noDueDateReason: null,
        status: 'Open',
        blockedReason: null,
        closedOn: null,
        severity: 'Error',
        originCommentCode: 'CMT-2026-0393',
        originDecisionCode: 'DEC-2026-0071',
      },
      {
        businessCode: 'ACT-2026-0182',
        title: 'Publish the slot-management ownership split to the affected teams',
        ownerIndex: 22,
        subjectProduct: 19,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(1),
        raisedOn: shiftDays(asOf, -16),
        dueOn: null,
        noDueDateReason:
          'Due when the seasonal planning window opens, which Network Planning has not yet fixed. A placeholder date would be a commitment nobody made.',
        status: 'Blocked',
        blockedReason:
          'Waiting on the winter schedule freeze date from Network Planning. Chased 14 May.',
        closedOn: null,
        severity: 'Warning',
        originCommentCode: 'CMT-2026-0381',
        originDecisionCode: 'DEC-2026-0068',
      },
      {
        businessCode: 'ACT-2026-0179',
        title: 'Restate the benefit figure on the assessment to a quarterly basis',
        ownerIndex: 9,
        subjectProduct: 6,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(3),
        raisedOn: shiftDays(asOf, -14),
        dueOn: shiftDays(asOf, -9),
        noDueDateReason: null,
        status: 'Done',
        blockedReason: null,
        closedOn: shiftDays(asOf, -9),
        severity: 'Info',
        originCommentCode: 'CMT-2026-0388',
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0176',
        title: 'Widen the health scoring window so a backlog dump cannot move a band',
        ownerIndex: 2,
        subjectProduct: 44,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(9),
        raisedOn: shiftDays(asOf, -8),
        dueOn: shiftDays(asOf, 21),
        noDueDateReason: null,
        status: 'InProgress',
        blockedReason: null,
        closedOn: null,
        severity: 'Info',
        originCommentCode: 'CMT-2026-0398',
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0171',
        title: 'Resolve the lifecycle disagreement between the registry and the CMDB',
        ownerIndex: 5,
        subjectProduct: 8,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(19),
        raisedOn: shiftDays(asOf, -34),
        dueOn: shiftDays(asOf, -12),
        noDueDateReason: null,
        status: 'Blocked',
        blockedReason:
          'The CMDB feed has been failing authentication since 6 May, so the disagreement cannot be re-tested against current data.',
        closedOn: null,
        severity: 'Error',
        originCommentCode: 'CMT-2026-0364',
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0168',
        title: 'Retire the duplicate ground handling integration record',
        ownerIndex: 7,
        subjectProduct: 52,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(7),
        raisedOn: shiftDays(asOf, -41),
        dueOn: shiftDays(asOf, -30),
        noDueDateReason: null,
        status: 'Done',
        blockedReason: null,
        closedOn: shiftDays(asOf, -31),
        severity: 'Info',
        originCommentCode: null,
        originDecisionCode: null,
      },
      {
        businessCode: 'ACT-2026-0163',
        title: 'Re-run the supplier concentration report after the contract is signed',
        ownerIndex: 14,
        subjectProduct: 33,
        subjectApplication: null,
        subjectMissing: null,
        unownedReason: null,
        raisedBy: personName(21),
        raisedOn: shiftDays(asOf, -48),
        dueOn: shiftDays(asOf, 30),
        noDueDateReason: null,
        status: 'Cancelled',
        blockedReason: null,
        closedOn: shiftDays(asOf, -19),
        severity: 'Info',
        originCommentCode: 'CMT-2026-0376',
        originDecisionCode: null,
      },
    ];

    return seeds.map((seed, position) => {
      const { ownerIndex, subjectProduct, subjectApplication, subjectMissing, ...rest } = seed;
      return {
        ...rest,
        id: `${dataset.enterprise.id.slice(0, 24)}${(5000 + position).toString().padStart(12, '0')}`,
        subject: {
          ref:
            subjectProduct !== null
              ? productRef(product(subjectProduct))
              : subjectApplication !== null
                ? applicationRef(application(subjectApplication))
                : null,
          missingReason: subjectMissing,
        },
        owner: ownerIndex === null ? null : ownerRef(person(ownerIndex), user),
      };
    });
  }

  // ── Decisions ──────────────────────────────────────────────────────────────

  function buildDecisions(): DecisionRecord[] {
    const seeds: Array<
      Omit<DecisionRecord, 'id' | 'subject' | 'decidedBy' | 'approvalRoute'> & {
        deciderIndex: number | null;
        subjectProduct: number | null;
        subjectApplication: number | null;
      }
    > = [
      {
        businessCode: 'DEC-2026-0074',
        title: 'Crew pairing and crew rostering are governed as one product',
        deciderIndex: 4,
        subjectProduct: 11,
        subjectApplication: null,
        status: 'Approved',
        decidedOn: shiftDays(asOf, -1),
        effectiveFrom: shiftDays(asOf, 11),
        rationale:
          'They share an owner, a funding line and a release train, and every application either supports both or neither. Governing them separately produced two hierarchies that disagreed about six applications and a cost split nobody could defend.',
        reversalPath:
          'Reversible by the Crew value stream lead until the effective date without re-approval; afterwards it requires a superseding decision at the portfolio board, because the merged funding line will already have been drawn against.',
        supersedesCode: null,
        supersededByCode: null,
        evidenceCount: 5,
        confidence: 'High',
        linkedActionCodes: ['ACT-2026-0186'],
      },
      {
        businessCode: 'DEC-2026-0071',
        title: 'Retire the legacy load-sheet integration on 30 June 2026',
        deciderIndex: 16,
        subjectProduct: null,
        subjectApplication: 41,
        status: 'Approved',
        decidedOn: shiftDays(asOf, -9),
        effectiveFrom: shiftDays(asOf, 41),
        rationale:
          'The replacement has carried production traffic for two quarters and the vendor support contract lapses at the end of June. Extending support for a system with a working replacement was priced at more than the remaining migration.',
        reversalPath:
          'Reversible up to 15 June by the Ground Operations owner, after which the vendor contract cannot be reinstated within the notice period and reversal means a new procurement.',
        supersedesCode: 'DEC-2025-0402',
        supersededByCode: null,
        evidenceCount: 8,
        confidence: 'High',
        linkedActionCodes: ['ACT-2026-0184'],
      },
      {
        businessCode: 'DEC-2026-0068',
        title: 'Slot management sits with network planning, not with the schedule builder',
        deciderIndex: 1,
        subjectProduct: 19,
        subjectApplication: null,
        status: 'Approved',
        decidedOn: shiftDays(asOf, -15),
        effectiveFrom: shiftDays(asOf, -15),
        rationale:
          'Accountability follows the regulatory obligation. Slot compliance is reported by network planning to the coordinator, so the capability is governed where the obligation already sits rather than where the code happens to run.',
        reversalPath:
          'Revisited at the seasonal planning review each October. Reversal requires the network planning director and the schedule product owner jointly, because the reporting line moves with it.',
        supersedesCode: null,
        supersededByCode: null,
        evidenceCount: 3,
        confidence: 'Medium',
        linkedActionCodes: ['ACT-2026-0182'],
      },
      {
        businessCode: 'DEC-2026-0066',
        title: 'Adopt the 2026.2 criticality scale for all customer-facing products',
        deciderIndex: 3,
        subjectProduct: 6,
        subjectApplication: null,
        status: 'InReview',
        decidedOn: null,
        effectiveFrom: shiftDays(asOf, 25),
        rationale:
          'The four-point scale collapses two bands that behave differently under an outage. The proposed five-point scale separates "degraded but flying" from "grounded", which is the distinction operations actually acts on.',
        reversalPath:
          'Not yet decided, so nothing to reverse. If approved, the prior scale remains queryable as version 2025.4 and reclassification is a bulk operation the steward can undo within the same effective period.',
        supersedesCode: null,
        supersededByCode: null,
        evidenceCount: 2,
        confidence: 'Medium',
        linkedActionCodes: [],
      },
      {
        businessCode: 'DEC-2026-0061',
        title: 'Hold the disputed lifecycle stage at Active pending source resolution',
        deciderIndex: 5,
        subjectProduct: 8,
        subjectApplication: null,
        status: 'Approved',
        decidedOn: shiftDays(asOf, -28),
        effectiveFrom: shiftDays(asOf, -28),
        rationale:
          'The registry and the CMDB disagree. Holding the published value at the more conservative of the two keeps the product in scope for patching and support while the disagreement is worked, which is the failure mode with the lower cost.',
        reversalPath:
          'Automatically lapses when the source conflict is closed by a steward; no separate reversal is needed. If the conflict is still open on 30 June it returns to the portfolio board.',
        supersedesCode: null,
        supersededByCode: null,
        evidenceCount: 4,
        confidence: 'Low',
        linkedActionCodes: ['ACT-2026-0171'],
      },
      {
        businessCode: 'DEC-2026-0057',
        title: 'Reject the proposed split of the loyalty platform into three products',
        deciderIndex: 9,
        subjectProduct: 23,
        subjectApplication: null,
        status: 'Rejected',
        decidedOn: shiftDays(asOf, -37),
        effectiveFrom: shiftDays(asOf, -37),
        rationale:
          'The split follows the current team structure rather than anything a customer or a funder would recognise. Three products with one backlog and one release train is an organisational chart with product labels on it.',
        reversalPath:
          'A fresh proposal may be submitted at any time with evidence of separable funding and separable outcomes; this rejection does not bar re-submission and is not a precedent.',
        supersedesCode: null,
        supersededByCode: null,
        evidenceCount: 6,
        confidence: 'High',
        linkedActionCodes: [],
      },
      {
        businessCode: 'DEC-2025-0402',
        title: 'Extend legacy load-sheet support to June 2026',
        deciderIndex: 16,
        subjectProduct: null,
        subjectApplication: 41,
        status: 'Approved',
        decidedOn: shiftDays(asOf, -197),
        effectiveFrom: shiftDays(asOf, -180),
        rationale:
          'The replacement had not carried a full peak season. Extending support for two quarters bought the evidence that the migration decision needed.',
        reversalPath: 'Superseded by DEC-2026-0071. No independent reversal path remains.',
        supersedesCode: null,
        supersededByCode: 'DEC-2026-0071',
        evidenceCount: 4,
        confidence: 'High',
        linkedActionCodes: [],
      },
    ];

    return seeds.map((seed, position) => {
      const { deciderIndex, subjectProduct, subjectApplication, ...rest } = seed;
      return {
        ...rest,
        id: `${dataset.enterprise.id.slice(0, 24)}${(6000 + position).toString().padStart(12, '0')}`,
        subject: {
          ref:
            subjectProduct !== null
              ? productRef(product(subjectProduct))
              : subjectApplication !== null
                ? applicationRef(application(subjectApplication))
                : null,
          missingReason: null,
        },
        decidedBy: deciderIndex === null ? null : ownerRef(person(deciderIndex), user),
        approvalRoute: ROUTES.adminCommandCenter,
      };
    });
  }

  // ── Watchlist ──────────────────────────────────────────────────────────────

  function buildFavourites(): FavouriteRow[] {
    const seeds: Array<{
      productIndex: number;
      days: number;
      note: string | null;
      state: string | null;
      tone: FavouriteRow['stateTone'];
    }> = [
      {
        productIndex: 3,
        days: 61,
        note: 'Board pack every month — check the cost allocation first.',
        state: 'At risk',
        tone: 'caution',
      },
      {
        productIndex: 11,
        days: 47,
        note: 'Boundary decision in flight.',
        state: 'Healthy',
        tone: 'positive',
      },
      {
        productIndex: 19,
        days: 33,
        note: null,
        state: 'Healthy',
        tone: 'positive',
      },
      {
        productIndex: 44,
        days: 21,
        note: 'Watching the health score after the scoring-window change.',
        state: 'Degraded',
        tone: 'critical',
      },
      {
        productIndex: 6,
        days: 15,
        note: null,
        state: null,
        tone: 'neutral',
      },
      {
        productIndex: 27,
        days: 9,
        note: 'Contract renewal in the autumn.',
        state: 'Healthy',
        tone: 'positive',
      },
    ];

    return seeds.map((seed, position) => ({
      id: `${dataset.enterprise.id.slice(0, 24)}${(7000 + position).toString().padStart(12, '0')}`,
      subject: productRef(product(seed.productIndex)),
      pinnedOn: shiftDays(asOf, -seed.days),
      note: seed.note,
      // Null rather than a placeholder band: this product has not been scored
      // since the scoring change, and "unscored" is not "healthy".
      stateLabel: seed.state,
      stateTone: seed.tone,
    }));
  }

  function buildWatchlists(): WatchlistGroup[] {
    const item = (
      position: number,
      subjectProduct: number | null,
      subjectApplication: number | null,
      watchedDays: number,
      reason: string,
      changeDays: number | null,
      changeSummary: string | null,
      severity: WatchlistGroup['items'][number]['severity'],
    ) => ({
      id: `${dataset.enterprise.id.slice(0, 24)}${(7100 + position).toString().padStart(12, '0')}`,
      subject:
        subjectProduct !== null
          ? productRef(product(subjectProduct))
          : applicationRef(application(subjectApplication ?? 0)),
      watchedSince: shiftDays(asOf, -watchedDays),
      watchReason: reason,
      lastChangeAt: changeDays === null ? null : momentBefore(asOf, changeDays, '05:30'),
      lastChangeSummary: changeSummary,
      severity,
    });

    return [
      {
        id: `${dataset.enterprise.id.slice(0, 24)}${(7200).toString().padStart(12, '0')}`,
        businessCode: 'WL-001',
        name: 'Peak season critical path',
        purpose:
          'Everything that has to hold through the summer schedule. Reviewed every Monday by operations and network planning together.',
        ownerName: personName(1),
        rules: [
          'Criticality is MissionCritical or BusinessCritical',
          'Value stream is Flight Operations or Network Planning',
          'Health has moved a band in the last 30 days',
        ],
        sharedWith: ['Flight Operations leadership', 'Network Planning', 'IT service management'],
        items: [
          item(
            0,
            11,
            null,
            47,
            'Crew boundary decision takes effect before the schedule freeze.',
            2,
            'Decision DEC-2026-0074 approved; effective in 11 days.',
            'Warning',
          ),
          item(
            1,
            19,
            null,
            33,
            'Slot compliance reporting depends on it.',
            15,
            'Ownership moved to Network Planning.',
            'Info',
          ),
          item(
            2,
            44,
            null,
            21,
            'Health dropped two bands in a single refresh.',
            6,
            'Incident backlog cleared; score recalculated but the band has not recovered.',
            'Error',
          ),
          item(
            3,
            null,
            41,
            60,
            'Retirement lands mid-season.',
            9,
            'Retirement decision approved for 30 June.',
            'Warning',
          ),
        ],
      },
      {
        id: `${dataset.enterprise.id.slice(0, 24)}${(7201).toString().padStart(12, '0')}`,
        businessCode: 'WL-002',
        name: 'Cost allocation gaps',
        purpose:
          'Products whose run cost is not fully allocated. Cleared before each quarterly finance review; the list should be empty by 30 June.',
        ownerName: personName(11),
        rules: ['Allocated share of run cost is below 90%'],
        sharedWith: ['Finance business partners'],
        items: [
          item(
            4,
            3,
            null,
            61,
            'Two regional carrier cost centres are unallocated.',
            2,
            'Comment CMT-2026-0411 raised; action ACT-2026-0188 in progress.',
            'Warning',
          ),
          item(
            5,
            6,
            null,
            28,
            'Benefit and funding line are on different periodicities.',
            9,
            'Restated to a quarterly basis; gap closed.',
            'Info',
          ),
          item(
            6,
            33,
            null,
            48,
            'Supplier spend sits against the contract, not the product.',
            null,
            null,
            'Info',
          ),
        ],
      },
      {
        id: `${dataset.enterprise.id.slice(0, 24)}${(7202).toString().padStart(12, '0')}`,
        businessCode: 'WL-003',
        name: 'My governance follow-ups',
        purpose:
          'Hand-picked. Records where I have an open comment, an assigned action or a decision I have to make.',
        ownerName: user.displayName,
        rules: [],
        sharedWith: [],
        items: [
          item(
            7,
            8,
            null,
            34,
            'Lifecycle disagreement is still open and the CMDB feed is down.',
            12,
            'Action ACT-2026-0171 blocked on the failing feed.',
            'Error',
          ),
          item(
            8,
            23,
            null,
            37,
            'Rejected split may be re-proposed with better evidence.',
            37,
            'Proposal rejected.',
            'Info',
          ),
        ],
      },
    ];
  }

  function buildNotifications(): NotificationRow[] {
    const seeds: Array<{
      code: string;
      days: number;
      time: string;
      channel: NotificationRow['channel'];
      category: string;
      headline: string;
      detail: string;
      severity: NotificationRow['severity'];
      subjectProduct: number | null;
      isRead: boolean;
      readDays?: number;
      ruleCode: string;
      ruleLabel: string;
      route: string | null;
    }> = [
      {
        code: 'NTF-2026-3391',
        days: 0,
        time: '06:12',
        channel: 'InApp',
        category: 'Source failure',
        headline: 'Aurora CMDB authentication has failed for four consecutive runs',
        detail:
          'Since 6 May. Application records are being served from the last good run; nothing from the failed runs has been merged into them.',
        severity: 'Error',
        subjectProduct: null,
        isRead: false,
        ruleCode: 'SUB-004',
        ruleLabel: 'Source failure on a system I depend on',
        route: ROUTES.adminIntegrations,
      },
      {
        code: 'NTF-2026-3388',
        days: 1,
        time: '08:20',
        channel: 'InApp',
        category: 'Decision',
        headline: 'DEC-2026-0074 approved — crew pairing and rostering merge in 11 days',
        detail:
          'Approved by the Crew value stream lead. Effective from the date shown; reversible without re-approval until then.',
        severity: 'Info',
        subjectProduct: 11,
        isRead: false,
        ruleCode: 'SUB-001',
        ruleLabel: 'Decision taken on a record I watch',
        route: null,
      },
      {
        code: 'NTF-2026-3384',
        days: 1,
        time: '14:55',
        channel: 'InApp',
        category: 'Action',
        headline: 'ACT-2026-0186 is overdue by one day',
        detail: 'Reconcile the crew product boundary between the registry and the CMDB.',
        severity: 'Warning',
        subjectProduct: 11,
        isRead: false,
        ruleCode: 'SUB-002',
        ruleLabel: 'An action assigned to me falls due',
        route: null,
      },
      {
        code: 'NTF-2026-3379',
        days: 2,
        time: '07:02',
        channel: 'Email',
        category: 'Comment',
        headline: `${personName(5)} mentioned you on ${product(3).name}`,
        detail: 'Run cost excludes the two regional carriers onboarded in March.',
        severity: 'Info',
        subjectProduct: 3,
        isRead: false,
        ruleCode: 'SUB-003',
        ruleLabel: 'I am mentioned in a comment',
        route: null,
      },
      {
        code: 'NTF-2026-3372',
        days: 3,
        time: '06:00',
        channel: 'Digest',
        category: 'Watchlist',
        headline: 'Peak season critical path — 3 changes yesterday',
        detail:
          'One decision approved, one health band unchanged after recalculation, one retirement date confirmed.',
        severity: 'Info',
        subjectProduct: null,
        isRead: true,
        readDays: 3,
        ruleCode: 'SUB-005',
        ruleLabel: 'Daily digest for watchlists I own',
        route: null,
      },
      {
        code: 'NTF-2026-3366',
        days: 4,
        time: '11:31',
        channel: 'InApp',
        category: 'Report',
        headline: 'Portfolio health weekly ran with partial data',
        detail:
          'The Finance ERP feed did not contribute. Cost columns are absent from the output rather than estimated.',
        severity: 'Warning',
        subjectProduct: null,
        isRead: true,
        readDays: 4,
        ruleCode: 'SUB-006',
        ruleLabel: 'A report I subscribe to completes',
        route: null,
      },
      {
        code: 'NTF-2026-3359',
        days: 6,
        time: '05:30',
        channel: 'InApp',
        category: 'Watchlist',
        headline: `${product(44).name} health band did not recover after the backlog cleared`,
        detail:
          'Score recalculated on the cleared backlog. The band is unchanged, so the earlier drop was not an artefact of the queue.',
        severity: 'Error',
        subjectProduct: 44,
        isRead: false,
        ruleCode: 'SUB-007',
        ruleLabel: 'Health band changes on a watched product',
        route: null,
      },
      {
        code: 'NTF-2026-3351',
        days: 8,
        time: '16:44',
        channel: 'InApp',
        category: 'Taxonomy',
        headline: 'Criticality scale 2026.2 entered review',
        detail:
          'Five-point scale replacing the four-point one. Nothing is reclassified until an effective date is set.',
        severity: 'Info',
        subjectProduct: null,
        isRead: true,
        readDays: 7,
        ruleCode: 'SUB-008',
        ruleLabel: 'A vocabulary I classify against changes version',
        route: null,
      },
      {
        code: 'NTF-2026-3344',
        days: 12,
        time: '09:08',
        channel: 'Email',
        category: 'Action',
        headline: 'ACT-2026-0171 blocked',
        detail:
          'The lifecycle disagreement cannot be re-tested while the CMDB feed is failing authentication.',
        severity: 'Warning',
        subjectProduct: 8,
        isRead: true,
        readDays: 11,
        ruleCode: 'SUB-002',
        ruleLabel: 'An action assigned to me falls due',
        route: null,
      },
      {
        code: 'NTF-2026-3338',
        days: 15,
        time: '13:12',
        channel: 'InApp',
        category: 'Decision',
        headline: 'DEC-2026-0068 approved — slot management moves to network planning',
        detail: 'Effective immediately. Revisited at the October seasonal planning review.',
        severity: 'Info',
        subjectProduct: 19,
        isRead: true,
        readDays: 15,
        ruleCode: 'SUB-001',
        ruleLabel: 'Decision taken on a record I watch',
        route: null,
      },
      {
        code: 'NTF-2026-3330',
        days: 19,
        time: '10:26',
        channel: 'Digest',
        category: 'Report',
        headline: 'Supplier concentration monthly was not delivered',
        detail:
          'The schedule is paused pending the contract signature. No output was produced and none is being held back.',
        severity: 'Warning',
        subjectProduct: null,
        isRead: true,
        readDays: 19,
        ruleCode: 'SUB-006',
        ruleLabel: 'A report I subscribe to completes',
        route: null,
      },
      {
        code: 'NTF-2026-3322',
        days: 26,
        time: '08:39',
        channel: 'InApp',
        category: 'Comment',
        headline: `${personName(7)} resolved a thread on ${product(52).name}`,
        detail: 'Ground handling integration confirmed in scope for the value stream review.',
        severity: 'Info',
        subjectProduct: 52,
        isRead: true,
        readDays: 26,
        ruleCode: 'SUB-003',
        ruleLabel: 'I am mentioned in a comment',
        route: null,
      },
    ];

    return seeds.map((seed, position) => ({
      id: `${dataset.enterprise.id.slice(0, 24)}${(7300 + position).toString().padStart(12, '0')}`,
      businessCode: seed.code,
      receivedAt: momentBefore(asOf, seed.days, seed.time),
      channel: seed.channel,
      category: seed.category,
      headline: seed.headline,
      detail: seed.detail,
      severity: seed.severity,
      subject: seed.subjectProduct === null ? null : productRef(product(seed.subjectProduct)),
      isRead: seed.isRead,
      readAt:
        seed.isRead && seed.readDays !== undefined
          ? momentBefore(asOf, seed.readDays, '18:00')
          : null,
      ruleCode: seed.ruleCode,
      ruleLabel: seed.ruleLabel,
      route: seed.route,
    }));
  }

  function buildSubscriptions(): SubscriptionRow[] {
    const seeds: Array<Omit<SubscriptionRow, 'id'>> = [
      {
        businessCode: 'SUB-001',
        label: 'Decision taken on a record I watch',
        event: 'Decision approved, rejected or superseded',
        scopeLabel: 'All watchlists I own or follow',
        channels: ['InApp', 'Email'],
        frequency: 'Immediate',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 1, '08:20'),
        firedLast30Days: 6,
      },
      {
        businessCode: 'SUB-002',
        label: 'An action assigned to me falls due',
        event: 'Action due within 2 days, or overdue',
        scopeLabel: 'Actions where I am the owner',
        channels: ['InApp', 'Email'],
        frequency: 'Immediate',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 1, '14:55'),
        firedLast30Days: 11,
      },
      {
        businessCode: 'SUB-003',
        label: 'I am mentioned in a comment',
        event: 'Mention',
        scopeLabel: 'Anywhere in the platform',
        channels: ['InApp', 'Email'],
        frequency: 'Immediate',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 2, '07:02'),
        firedLast30Days: 9,
      },
      {
        businessCode: 'SUB-004',
        label: 'Source failure on a system I depend on',
        event: 'Connector health becomes Down or Degraded',
        scopeLabel: 'Sources feeding my products',
        channels: ['InApp'],
        frequency: 'Immediate',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 0, '06:12'),
        firedLast30Days: 14,
      },
      {
        businessCode: 'SUB-005',
        label: 'Daily digest for watchlists I own',
        event: 'Any change to a watched record',
        scopeLabel: 'WL-001, WL-002, WL-003',
        channels: ['Digest'],
        frequency: 'DailyDigest',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 3, '06:00'),
        firedLast30Days: 27,
      },
      {
        businessCode: 'SUB-006',
        label: 'A report I subscribe to completes',
        event: 'Scheduled report run finishes, whatever the outcome',
        scopeLabel: '4 report subscriptions',
        channels: ['InApp', 'Digest'],
        frequency: 'DailyDigest',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 4, '11:31'),
        firedLast30Days: 8,
      },
      {
        businessCode: 'SUB-007',
        label: 'Health band changes on a watched product',
        event: 'Product health moves a band in either direction',
        scopeLabel: 'WL-001',
        channels: ['InApp'],
        frequency: 'Immediate',
        mutedUntil: shiftDays(asOf, 4),
        lastFiredAt: momentBefore(asOf, 6, '05:30'),
        firedLast30Days: 5,
      },
      {
        businessCode: 'SUB-008',
        label: 'A vocabulary I classify against changes version',
        event: 'Taxonomy version enters review, is approved or takes effect',
        scopeLabel: 'Criticality, Lifecycle, Classification',
        channels: ['InApp'],
        frequency: 'WeeklyDigest',
        mutedUntil: null,
        lastFiredAt: momentBefore(asOf, 8, '16:44'),
        firedLast30Days: 3,
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(7400 + position).toString().padStart(12, '0')}`,
    }));
  }

  // ── Reports ────────────────────────────────────────────────────────────────

  function run(
    position: number,
    input: Omit<ReportRunRow, 'id' | 'durationMs'> & { durationMs: number | null },
  ): ReportRunRow {
    return {
      ...input,
      id: `${dataset.enterprise.id.slice(0, 24)}${(8000 + position).toString().padStart(12, '0')}`,
    };
  }

  function buildRuns(): ReportRunRow[] {
    return [
      run(0, {
        runCode: 'RUN-2026-2214',
        reportCode: 'RPT-001',
        reportName: 'Portfolio health weekly',
        startedAt: momentBefore(asOf, 4, '06:00'),
        finishedAt: momentBefore(asOf, 4, '06:07'),
        durationMs: 421_000,
        status: 'PartialData',
        outcome:
          'Completed without the Finance ERP feed. Cost columns are absent from the output; no estimate was substituted for them.',
        rowCount: 142,
        dataAsOf: shiftDays(asOf, -5),
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(1, {
        runCode: 'RUN-2026-2210',
        reportCode: 'RPT-003',
        reportName: 'Application rationalisation candidates',
        startedAt: momentBefore(asOf, 5, '02:00'),
        finishedAt: momentBefore(asOf, 5, '02:19'),
        durationMs: 1_143_000,
        status: 'Succeeded',
        outcome: 'Delivered to 34 recipients. 1,760 applications assessed, 211 flagged.',
        rowCount: 211,
        dataAsOf: shiftDays(asOf, -5),
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(2, {
        runCode: 'RUN-2026-2207',
        reportCode: 'RPT-002',
        reportName: 'Investment and benefit realisation',
        startedAt: momentBefore(asOf, 6, '05:00'),
        finishedAt: momentBefore(asOf, 6, '05:02'),
        durationMs: 128_000,
        status: 'Failed',
        outcome:
          'The Finance ERP extract was unavailable and the report refused to run against a partial ledger. No output was produced; the previous edition has not been re-dated.',
        rowCount: null,
        dataAsOf: null,
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(3, {
        runCode: 'RUN-2026-2203',
        reportCode: 'RPT-101',
        reportName: 'Value stream drill-down (on demand)',
        startedAt: momentBefore(asOf, 7, '15:41'),
        finishedAt: momentBefore(asOf, 7, '15:44'),
        durationMs: 172_000,
        status: 'Succeeded',
        outcome: `Generated by ${personName(1)} for the Network Planning review.`,
        rowCount: 38,
        dataAsOf: shiftDays(asOf, -8),
        triggeredBy: personName(1),
        isScheduled: false,
      }),
      run(4, {
        runCode: 'RUN-2026-2198',
        reportCode: 'RPT-004',
        reportName: 'Governance queue and SLA',
        startedAt: momentBefore(asOf, 11, '06:00'),
        finishedAt: momentBefore(asOf, 11, '06:04'),
        durationMs: 233_000,
        status: 'Succeeded',
        outcome: 'Delivered to 12 recipients. Three service levels reported not on track.',
        rowCount: 4,
        dataAsOf: shiftDays(asOf, -11),
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(5, {
        runCode: 'RUN-2026-2191',
        reportCode: 'RPT-102',
        reportName: 'Supplier exposure by product (on demand)',
        startedAt: momentBefore(asOf, 14, '10:22'),
        finishedAt: null,
        durationMs: null,
        status: 'Cancelled',
        outcome: `Cancelled by ${personName(21)} after the parameters were corrected. Nothing was delivered.`,
        rowCount: null,
        dataAsOf: null,
        triggeredBy: personName(21),
        isScheduled: false,
      }),
      run(6, {
        runCode: 'RUN-2026-2186',
        reportCode: 'RPT-001',
        reportName: 'Portfolio health weekly',
        startedAt: momentBefore(asOf, 11, '06:00'),
        finishedAt: momentBefore(asOf, 11, '06:06'),
        durationMs: 366_000,
        status: 'Succeeded',
        outcome: 'Delivered to 58 recipients. All contributing sources responded.',
        rowCount: 142,
        dataAsOf: shiftDays(asOf, -12),
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(7, {
        runCode: 'RUN-2026-2180',
        reportCode: 'RPT-005',
        reportName: 'Supplier concentration monthly',
        startedAt: momentBefore(asOf, 19, '07:00'),
        finishedAt: momentBefore(asOf, 19, '07:00'),
        durationMs: 4_000,
        status: 'Cancelled',
        outcome:
          'The schedule was paused before this run began, pending the contract signature. No output was produced.',
        rowCount: null,
        dataAsOf: null,
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(8, {
        runCode: 'RUN-2026-2174',
        reportCode: 'RPT-002',
        reportName: 'Investment and benefit realisation',
        startedAt: momentBefore(asOf, 34, '05:00'),
        finishedAt: momentBefore(asOf, 34, '05:11'),
        durationMs: 664_000,
        status: 'Succeeded',
        outcome: 'Delivered to 21 recipients. This is the newest complete edition.',
        rowCount: 142,
        dataAsOf: shiftDays(asOf, -35),
        triggeredBy: 'Schedule',
        isScheduled: true,
      }),
      run(9, {
        runCode: 'RUN-2026-2169',
        reportCode: 'RPT-103',
        reportName: 'Taxonomy change impact (on demand)',
        startedAt: momentBefore(asOf, 8, '17:03'),
        finishedAt: momentBefore(asOf, 8, '17:05'),
        durationMs: 96_000,
        status: 'Succeeded',
        outcome: `Generated by ${personName(3)} against draft criticality version 2026.2.`,
        rowCount: 486,
        dataAsOf: shiftDays(asOf, -8),
        triggeredBy: personName(3),
        isScheduled: false,
      }),
    ];
  }

  function buildScheduled(runs: ReportRunRow[]): ScheduledReportRow[] {
    const latest = (code: string): ReportRunRow | null =>
      runs.filter((r) => r.reportCode === code).at(0) ?? null;

    const seeds: Array<
      Omit<ScheduledReportRow, 'id' | 'lastRun' | 'isStale' | 'staleReason'> & {
        /** Days after which the newest good output stops being current. */
        freshnessDays: number;
      }
    > = [
      {
        businessCode: 'RPT-001',
        name: 'Portfolio health weekly',
        purpose:
          'Health, movement and open exceptions across every governed product, for the Monday leadership review.',
        audience: 'Portfolio leadership, value stream leads',
        cadenceLabel: 'Every Monday, 06:00 UTC',
        nextRunAt: momentAfter(asOf, 3, '06:00'),
        pausedReason: null,
        ownerName: personName(1),
        format: 'PDF and XLSX',
        deliveryLabel: 'Email to a distribution list',
        recipientCount: 58,
        freshnessDays: 7,
      },
      {
        businessCode: 'RPT-002',
        name: 'Investment and benefit realisation',
        purpose:
          'Spend against funding line and realised benefit against target, by product and value stream.',
        audience: 'Finance business partners, portfolio board',
        cadenceLabel: 'First working day of the month, 05:00 UTC',
        nextRunAt: momentAfter(asOf, 12, '05:00'),
        pausedReason: null,
        ownerName: personName(11),
        format: 'XLSX',
        deliveryLabel: 'Email, plus a copy to the finance drop',
        recipientCount: 21,
        freshnessDays: 31,
      },
      {
        businessCode: 'RPT-003',
        name: 'Application rationalisation candidates',
        purpose:
          'Applications with overlap, low usage or an expiring contract, ranked by the cost of keeping them.',
        audience: 'Architecture review board',
        cadenceLabel: 'Every second Wednesday, 02:00 UTC',
        nextRunAt: momentAfter(asOf, 9, '02:00'),
        pausedReason: null,
        ownerName: personName(4),
        format: 'XLSX',
        deliveryLabel: 'Email',
        recipientCount: 34,
        freshnessDays: 14,
      },
      {
        businessCode: 'RPT-004',
        name: 'Governance queue and SLA',
        purpose:
          'Queue depth, ageing and service level attainment across every governance worklist.',
        audience: 'Data stewards, governance council',
        cadenceLabel: 'Every Monday, 06:00 UTC',
        nextRunAt: momentAfter(asOf, 3, '06:00'),
        pausedReason: null,
        ownerName: personName(5),
        format: 'PDF',
        deliveryLabel: 'Email',
        recipientCount: 12,
        freshnessDays: 7,
      },
      {
        businessCode: 'RPT-005',
        name: 'Supplier concentration monthly',
        purpose: 'Spend concentration by supplier, with the top five as a share of the total.',
        audience: 'Sourcing, finance',
        cadenceLabel: 'Monthly, 07:00 UTC on the 20th',
        nextRunAt: null,
        pausedReason:
          'Paused by the sourcing lead on 1 May pending the contract signature, so the figures cannot be misread as final terms. Nothing has been produced and nothing is being withheld.',
        ownerName: personName(21),
        format: 'PDF',
        deliveryLabel: 'Email',
        recipientCount: 9,
        freshnessDays: 31,
      },
      {
        businessCode: 'RPT-006',
        name: 'Crew capacity and roster stability',
        purpose:
          'Roster churn, standby usage and crew capacity against the published schedule, by base.',
        audience: 'Crew operations',
        cadenceLabel: 'Every Friday, 04:00 UTC',
        nextRunAt: momentAfter(asOf, 7, '04:00'),
        pausedReason: null,
        ownerName: personName(12),
        format: 'XLSX',
        deliveryLabel: 'Email',
        recipientCount: 17,
        freshnessDays: 7,
      },
    ];

    return seeds.map((seed, position) => {
      const { freshnessDays, ...rest } = seed;
      const lastRun = latest(seed.businessCode);
      const goodOutput =
        lastRun && (lastRun.status === 'Succeeded' || lastRun.status === 'PartialData')
          ? lastRun
          : (runs.find(
              (r) =>
                r.reportCode === seed.businessCode &&
                (r.status === 'Succeeded' || r.status === 'PartialData'),
            ) ?? null);

      const ageDays =
        goodOutput?.dataAsOf === undefined || goodOutput?.dataAsOf === null
          ? null
          : daysApart(goodOutput.dataAsOf, asOf);

      const isStale = ageDays === null ? true : ageDays > freshnessDays;

      return {
        ...rest,
        id: `${dataset.enterprise.id.slice(0, 24)}${(8100 + position).toString().padStart(12, '0')}`,
        lastRun,
        isStale,
        staleReason: !isStale
          ? null
          : ageDays === null
            ? 'No run of this report has ever produced an output, so there is no edition to be current or otherwise.'
            : `The newest complete edition describes ${goodOutput?.dataAsOf}, which is ${ageDays} days old against a ${freshnessDays}-day freshness promise. It is readable, but it is not this week's picture.`,
      };
    });
  }

  function buildOnDemand(runs: ReportRunRow[]): OnDemandReportRow[] {
    const latest = (code: string): ReportRunRow | null =>
      runs.find((r) => r.reportCode === code) ?? null;

    const seeds: Array<Omit<OnDemandReportRow, 'id' | 'lastRun'>> = [
      {
        businessCode: 'RPT-101',
        name: 'Value stream drill-down',
        purpose:
          'Every product, application and team beneath one value stream, with health and cost side by side.',
        parameters: ['Value stream', 'Period', 'Include retired records'],
        estimatedRuntimeLabel: 'About 3 minutes',
        ownerName: personName(1),
        restrictedReason: null,
      },
      {
        businessCode: 'RPT-102',
        name: 'Supplier exposure by product',
        purpose:
          'Contracted spend and notice periods by supplier, attributed to the products they support.',
        parameters: ['Supplier', 'Period', 'Minimum contract value'],
        estimatedRuntimeLabel: 'About 6 minutes',
        ownerName: personName(21),
        restrictedReason: user.canSeeRestrictedFinance
          ? null
          : 'This report contains contracted values. Your role does not carry restricted finance rights, so it cannot be prepared for you — ask a finance business partner.',
      },
      {
        businessCode: 'RPT-103',
        name: 'Taxonomy change impact',
        purpose:
          'Which records carry a term that a proposed version would change, and what the migration rule would do to them.',
        parameters: ['Vocabulary', 'From version', 'To version'],
        estimatedRuntimeLabel: 'Under 2 minutes',
        ownerName: personName(3),
        restrictedReason: null,
      },
      {
        businessCode: 'RPT-104',
        name: 'Decision register extract',
        purpose:
          'Every decision in a period with its rationale, effective date and reversal path, for audit.',
        parameters: ['Period', 'Status', 'Value stream'],
        estimatedRuntimeLabel: 'Under 1 minute',
        ownerName: personName(5),
        restrictedReason: null,
      },
      {
        businessCode: 'RPT-105',
        name: 'Unallocated cost by owner',
        purpose:
          'Run cost with no approved allocation, attributed to the single owner accountable for closing the gap.',
        parameters: ['Period', 'Threshold'],
        estimatedRuntimeLabel: 'About 4 minutes',
        ownerName: personName(11),
        restrictedReason: user.canSeeRestrictedFinance
          ? null
          : 'This report contains cost values. Your role does not carry restricted finance rights, so it cannot be prepared for you.',
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(8200 + position).toString().padStart(12, '0')}`,
      lastRun: latest(seed.businessCode),
    }));
  }

  // ── Taxonomy ───────────────────────────────────────────────────────────────

  function buildVocabularies(): TaxonomyVocabularyRow[] {
    const seeds: Array<Omit<TaxonomyVocabularyRow, 'id'>> = [
      {
        businessCode: 'VOC-CRIT',
        name: 'Criticality',
        purpose:
          'How badly the airline is hurt when this stops. Drives the recovery objective and the on-call tier.',
        ownerName: personName(3),
        termCount: 4,
        inForceVersion: '2025.4',
        inForceSince: shiftDays(asOf, -171),
        pendingVersion: '2026.2',
        pendingStatus: 'InReview',
        pendingEffectiveFrom: null,
        lastChangedAt: momentBefore(asOf, 8, '16:44'),
        governedEntityCount: 1902,
      },
      {
        businessCode: 'VOC-LIFE',
        name: 'Lifecycle',
        purpose:
          'Where a product or application sits between proposal and decommission. Governs whether it may take new investment.',
        ownerName: personName(5),
        termCount: 6,
        inForceVersion: '2026.1',
        inForceSince: shiftDays(asOf, -140),
        pendingVersion: '2026.3',
        pendingStatus: 'Scheduled',
        pendingEffectiveFrom: shiftDays(asOf, 42),
        lastChangedAt: momentBefore(asOf, 21, '10:12'),
        governedEntityCount: 1902,
      },
      {
        businessCode: 'VOC-CLASS',
        name: 'Product classification',
        purpose:
          'Customer-facing, enabling, supporting or platform. Decides which value model a product is measured by.',
        ownerName: personName(1),
        termCount: 4,
        inForceVersion: '2025.2',
        inForceSince: shiftDays(asOf, -412),
        pendingVersion: null,
        pendingStatus: null,
        pendingEffectiveFrom: null,
        lastChangedAt: momentBefore(asOf, 412, '09:00'),
        governedEntityCount: 142,
      },
      {
        businessCode: 'VOC-VS',
        name: 'Value stream',
        purpose: 'The end-to-end flows the airline organises product investment around.',
        ownerName: personName(1),
        termCount: 9,
        inForceVersion: '2026.1',
        inForceSince: shiftDays(asOf, -140),
        pendingVersion: '2026.4',
        pendingStatus: 'Draft',
        pendingEffectiveFrom: null,
        lastChangedAt: momentBefore(asOf, 3, '11:38'),
        governedEntityCount: 142,
      },
      {
        businessCode: 'VOC-DQ',
        name: 'Data quality dimension',
        purpose:
          'Completeness, validity, uniqueness, consistency, timeliness and accuracy, as this platform defines them.',
        ownerName: personName(5),
        termCount: 6,
        inForceVersion: '2025.1',
        inForceSince: shiftDays(asOf, -505),
        pendingVersion: null,
        pendingStatus: null,
        pendingEffectiveFrom: null,
        lastChangedAt: momentBefore(asOf, 505, '09:00'),
        governedEntityCount: 96,
      },
      {
        businessCode: 'VOC-BEN',
        name: 'Benefit type',
        purpose:
          'Cost avoidance, revenue uplift, risk reduction or capacity release. Determines how a benefit may be claimed.',
        ownerName: personName(11),
        termCount: 5,
        inForceVersion: '2026.1',
        inForceSince: shiftDays(asOf, -140),
        pendingVersion: '2026.2',
        pendingStatus: 'Rejected',
        pendingEffectiveFrom: null,
        lastChangedAt: momentBefore(asOf, 55, '14:20'),
        governedEntityCount: 318,
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(9000 + position).toString().padStart(12, '0')}`,
    }));
  }

  function buildVersions(): TaxonomyVersionRow[] {
    const seeds: Array<Omit<TaxonomyVersionRow, 'id'>> = [
      {
        vocabularyCode: 'VOC-CRIT',
        versionLabel: '2026.2',
        status: 'InReview',
        effectiveFrom: null,
        effectiveTo: null,
        approvedBy: null,
        approvedOn: null,
        pendingReason:
          'Awaiting the operations director. Two of three approvals recorded; the review has been open 8 days against a 10-day service level.',
        changeCount: 4,
        summary: 'Splits Critical into Grounding and Degraded, and renames Standard to Routine.',
      },
      {
        vocabularyCode: 'VOC-CRIT',
        versionLabel: '2025.4',
        status: 'InForce',
        effectiveFrom: shiftDays(asOf, -171),
        effectiveTo: null,
        approvedBy: personName(3),
        approvedOn: shiftDays(asOf, -178),
        pendingReason: null,
        changeCount: 2,
        summary: 'Four-point scale. Definitions aligned to the recovery time objectives.',
      },
      {
        vocabularyCode: 'VOC-CRIT',
        versionLabel: '2025.1',
        status: 'Superseded',
        effectiveFrom: shiftDays(asOf, -505),
        effectiveTo: shiftDays(asOf, -172),
        approvedBy: personName(3),
        approvedOn: shiftDays(asOf, -512),
        pendingReason: null,
        changeCount: 5,
        summary: 'First governed criticality scale, migrated from the CMDB free-text field.',
      },
      {
        vocabularyCode: 'VOC-LIFE',
        versionLabel: '2026.3',
        status: 'Scheduled',
        effectiveFrom: shiftDays(asOf, 42),
        effectiveTo: null,
        approvedBy: personName(5),
        approvedOn: shiftDays(asOf, -6),
        pendingReason: null,
        changeCount: 3,
        summary:
          'Adds Sunset between Active and Decommissioned so a product can stop taking investment without being switched off.',
      },
      {
        vocabularyCode: 'VOC-LIFE',
        versionLabel: '2026.1',
        status: 'InForce',
        effectiveFrom: shiftDays(asOf, -140),
        effectiveTo: shiftDays(asOf, 41),
        approvedBy: personName(5),
        approvedOn: shiftDays(asOf, -149),
        pendingReason: null,
        changeCount: 2,
        summary: 'Six stages. Proposed and Retired given explicit entry criteria.',
      },
      {
        vocabularyCode: 'VOC-VS',
        versionLabel: '2026.4',
        status: 'Draft',
        effectiveFrom: null,
        effectiveTo: null,
        approvedBy: null,
        approvedOn: null,
        pendingReason:
          'Draft. Not submitted for review, so no approver has seen it and nothing about it is agreed.',
        changeCount: 2,
        summary:
          'Proposes splitting Customer Experience into Booking and Journey. Working document only.',
      },
      {
        vocabularyCode: 'VOC-VS',
        versionLabel: '2026.1',
        status: 'InForce',
        effectiveFrom: shiftDays(asOf, -140),
        effectiveTo: null,
        approvedBy: personName(1),
        approvedOn: shiftDays(asOf, -147),
        pendingReason: null,
        changeCount: 1,
        summary: 'Nine value streams. Cargo separated from Ground Operations.',
      },
      {
        vocabularyCode: 'VOC-BEN',
        versionLabel: '2026.2',
        status: 'Rejected',
        effectiveFrom: null,
        effectiveTo: null,
        approvedBy: personName(11),
        approvedOn: shiftDays(asOf, -55),
        pendingReason: null,
        changeCount: 3,
        summary:
          'Proposed a Strategic Optionality benefit type. Rejected: no measurable claim could be defined for it.',
      },
      {
        vocabularyCode: 'VOC-BEN',
        versionLabel: '2026.1',
        status: 'InForce',
        effectiveFrom: shiftDays(asOf, -140),
        effectiveTo: null,
        approvedBy: personName(11),
        approvedOn: shiftDays(asOf, -151),
        pendingReason: null,
        changeCount: 2,
        summary: 'Five benefit types, each with a required evidence class.',
      },
      {
        vocabularyCode: 'VOC-CLASS',
        versionLabel: '2025.2',
        status: 'InForce',
        effectiveFrom: shiftDays(asOf, -412),
        effectiveTo: null,
        approvedBy: personName(1),
        approvedOn: shiftDays(asOf, -420),
        pendingReason: null,
        changeCount: 1,
        summary: 'Four classes. Platform separated from Enabling.',
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(9100 + position).toString().padStart(12, '0')}`,
    }));
  }

  function buildChanges(): TaxonomyChangeRow[] {
    const seeds: Array<Omit<TaxonomyChangeRow, 'id'>> = [
      {
        vocabularyCode: 'VOC-CRIT',
        fromVersion: '2025.4',
        toVersion: '2026.2',
        changeType: 'Added',
        term: 'Grounding',
        before: null,
        after: 'An outage that stops aircraft departing. Recovery objective 15 minutes.',
        affectedRecordCount: 0,
        migrationRule:
          'Nothing is auto-assigned. Every record currently Critical is reviewed by its owner before the effective date.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-CRIT',
        fromVersion: '2025.4',
        toVersion: '2026.2',
        changeType: 'DefinitionChanged',
        term: 'Critical',
        before: 'Severe disruption to flight operations. Recovery objective 1 hour.',
        after: 'Renamed to Degraded. Disruption that does not stop departures. Recovery 1 hour.',
        affectedRecordCount: 486,
        migrationRule:
          'Records keep their band and take the new label. Owners of the 61 records flagged as departure-blocking are asked to reclassify to Grounding.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-CRIT',
        fromVersion: '2025.4',
        toVersion: '2026.2',
        changeType: 'Renamed',
        term: 'Standard',
        before: 'Standard',
        after: 'Routine',
        affectedRecordCount: 1104,
        migrationRule: 'Label only. No record changes band and no report changes shape.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-CRIT',
        fromVersion: '2025.4',
        toVersion: '2026.2',
        changeType: 'Deprecated',
        term: 'Non-critical',
        before: 'Non-critical',
        after: 'Deprecated. Retained for as-of queries before the effective date.',
        affectedRecordCount: 312,
        migrationRule: null,
        migrationGapReason:
          'No rule agreed. Whether these become Routine or a new Low band is exactly what the review is still arguing about, and inventing a default here would settle it by accident.',
      },
      {
        vocabularyCode: 'VOC-LIFE',
        fromVersion: '2026.1',
        toVersion: '2026.3',
        changeType: 'Added',
        term: 'Sunset',
        before: null,
        after: 'Live, supported, closed to new investment. Sits between Active and Decommissioned.',
        affectedRecordCount: 0,
        migrationRule:
          'Applied on request by the owner. Nothing moves into Sunset automatically on the effective date.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-LIFE',
        fromVersion: '2026.1',
        toVersion: '2026.3',
        changeType: 'DefinitionChanged',
        term: 'Active',
        before: 'In production use.',
        after: 'In production use and open to new investment.',
        affectedRecordCount: 1284,
        migrationRule:
          'No record moves. The narrowed definition applies from the effective date forward; prior classifications stay valid for as-of reporting.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-LIFE',
        fromVersion: '2026.1',
        toVersion: '2026.3',
        changeType: 'Merged',
        term: 'Retired',
        before: 'Retired',
        after: 'Merged into Decommissioned.',
        affectedRecordCount: 97,
        migrationRule:
          'Bulk update on the effective date, reversible by the steward within the same effective period.',
        migrationGapReason: null,
      },
      {
        vocabularyCode: 'VOC-VS',
        fromVersion: '2026.1',
        toVersion: '2026.4',
        changeType: 'Added',
        term: 'Journey',
        before: null,
        after: 'Everything after the booking is confirmed, up to arrival.',
        affectedRecordCount: 0,
        migrationRule: null,
        migrationGapReason:
          'This is a draft. No migration is defined because nothing has been agreed to migrate to.',
      },
      {
        vocabularyCode: 'VOC-VS',
        fromVersion: '2026.1',
        toVersion: '2026.4',
        changeType: 'DefinitionChanged',
        term: 'Customer Experience',
        before: 'Booking through to arrival.',
        after: 'Renamed Booking. Search, offer and payment only.',
        affectedRecordCount: 31,
        migrationRule: null,
        migrationGapReason:
          'This is a draft. Reassigning 31 products between value streams needs the two value stream leads to agree first.',
      },
      {
        vocabularyCode: 'VOC-BEN',
        fromVersion: '2026.1',
        toVersion: '2026.2',
        changeType: 'Added',
        term: 'Strategic optionality',
        before: null,
        after: 'Rejected. Never took effect.',
        affectedRecordCount: 0,
        migrationRule: 'None. The version was rejected, so no record was ever classified this way.',
        migrationGapReason: null,
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(9200 + position).toString().padStart(12, '0')}`,
    }));
  }

  function buildPolicies(): PolicyVersionRow[] {
    const seeds: Array<Omit<PolicyVersionRow, 'id'>> = [
      {
        businessCode: 'POL-001',
        name: 'Ownership completeness',
        domain: 'Stewardship',
        enforcement: 'Blocking',
        inForceVersion: 'v4',
        effectiveFrom: shiftDays(asOf, -140),
        nextReviewOn: shiftDays(asOf, 52),
        overdueReviewReason: null,
        ownerName: personName(5),
        pendingVersion: null,
        pendingStatus: null,
        breachCount30Days: 0,
      },
      {
        businessCode: 'POL-002',
        name: 'Cost allocation before publication',
        domain: 'Finance',
        enforcement: 'Blocking',
        inForceVersion: 'v2',
        effectiveFrom: shiftDays(asOf, -320),
        nextReviewOn: shiftDays(asOf, -14),
        overdueReviewReason:
          'The review fell due on the day the finance business partner changed. It is 14 days overdue and the policy remains in force meanwhile — an unreviewed policy is not a suspended one.',
        ownerName: personName(11),
        pendingVersion: 'v3',
        pendingStatus: 'InReview',
        breachCount30Days: 6,
      },
      {
        businessCode: 'POL-003',
        name: 'Evidence age for benefit claims',
        domain: 'Value',
        enforcement: 'Advisory',
        inForceVersion: 'v3',
        effectiveFrom: shiftDays(asOf, -212),
        nextReviewOn: shiftDays(asOf, 118),
        overdueReviewReason: null,
        ownerName: personName(9),
        pendingVersion: null,
        pendingStatus: null,
        breachCount30Days: 23,
      },
      {
        businessCode: 'POL-004',
        name: 'Relationship confidence threshold',
        domain: 'Mapping',
        enforcement: 'Advisory',
        inForceVersion: 'v5',
        effectiveFrom: shiftDays(asOf, -63),
        nextReviewOn: shiftDays(asOf, 129),
        overdueReviewReason: null,
        ownerName: personName(4),
        pendingVersion: 'v6',
        pendingStatus: 'Draft',
        breachCount30Days: 41,
      },
      {
        businessCode: 'POL-005',
        name: 'Restricted field disclosure',
        domain: 'Access',
        enforcement: 'Blocking',
        inForceVersion: 'v7',
        effectiveFrom: shiftDays(asOf, -28),
        nextReviewOn: shiftDays(asOf, 155),
        overdueReviewReason: null,
        ownerName: personName(3),
        pendingVersion: null,
        pendingStatus: null,
        breachCount30Days: 0,
      },
    ];

    return seeds.map((seed, position) => ({
      ...seed,
      id: `${dataset.enterprise.id.slice(0, 24)}${(9300 + position).toString().padStart(12, '0')}`,
    }));
  }

  // ── Repository ─────────────────────────────────────────────────────────────

  return {
    async getCollaboration(
      filters: GlobalFilters,
    ): Promise<QueryResult<CollaborationWorkspaceView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const comments = buildComments();
      const actions = buildActions();
      const decisions = buildDecisions();

      const openActions = actions.filter(
        (action) => action.status !== 'Done' && action.status !== 'Cancelled',
      );
      const overdue = openActions.filter(
        (action) => action.dueOn !== null && action.dueOn < asOf,
      ).length;

      const view: CollaborationWorkspaceView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-D13-01',
            label: 'Open Actions',
            value: openActions.length,
            unit: 'count',
            comparisonValue: openActions.length + 2,
            comparisonLabel: 'vs 30 days ago',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-02',
            label: 'Overdue Actions',
            value: overdue,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-03',
            label: 'Unresolved Threads',
            value: comments.filter((comment) => comment.resolvedAt === null).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-04',
            label: 'Decisions In Flight',
            value: decisions.filter(
              (decision) => decision.status === 'InReview' || decision.status === 'Submitted',
            ).length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-05',
            label: 'Decisions Effective Soon',
            value: decisions.filter(
              (decision) =>
                decision.effectiveFrom !== null &&
                decision.effectiveFrom > asOf &&
                daysApart(asOf, decision.effectiveFrom) <= 30,
            ).length,
            unit: 'count',
            confidence: 'High',
          }),
        ],
        comments,
        actions,
        decisions,
        governanceConsoleRoute: ROUTES.adminCommandCenter,
        auditTrailRoute: ROUTES.adminCommandCenter,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminCommandCenter,
          sources,
          confidence: 'High',
          actions: [
            {
              action: 'collaboration.comment',
              permission: 'create',
              enabled: false,
              disabledReason:
                // Customer-facing copy: no "in this build", no confession of
                // missing plumbing. Say what the reader can do and what would
                // change it, in their language.
                'Comments and decisions are captured in the systems where the work happens and appear here as a governed record. Connect a collaboration feed to post from this screen.',
            },
            {
              action: 'collaboration.decide',
              permission: 'approve',
              enabled: false,
              disabledReason:
                'Decisions are recorded through the governance console once the approval workflow is connected.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },

    async getWatchlist(filters: GlobalFilters): Promise<QueryResult<WatchlistView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const notifications = buildNotifications();
      const watchlists = buildWatchlists();
      const favourites = buildFavourites();

      const view: WatchlistView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-D13-11',
            label: 'Unread Notifications',
            value: notifications.filter((notification) => !notification.isRead).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-12',
            label: 'Watched Records',
            value: watchlists.reduce((sum, list) => sum + list.items.length, 0),
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-13',
            label: 'Favourites',
            value: favourites.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-14',
            label: 'Alerts Needing Attention',
            value: notifications.filter(
              (notification) => !notification.isRead && notification.severity !== 'Info',
            ).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
        ],
        favourites,
        watchlists,
        notifications,
        subscriptions: buildSubscriptions(),
        notificationRulesRoute: ROUTES.adminCommandCenter,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminCommandCenter,
          sources,
          confidence: 'High',
          actions: [
            { action: 'watchlist.markRead', permission: 'edit', enabled: true },
            {
              action: 'watchlist.edit',
              permission: 'edit',
              enabled: false,
              disabledReason:
                'Watchlist membership is not editable in this build; the subscription service is not connected.',
            },
          ],
        }),
      });
    },

    async getReports(filters: GlobalFilters): Promise<QueryResult<ReportsAnalyticsView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const runs = buildRuns();
      const scheduled = buildScheduled(runs);
      const onDemand = buildOnDemand(runs);

      const succeeded = runs.filter((r) => r.status === 'Succeeded').length;
      const partial = runs.filter((r) => r.status === 'PartialData').length;
      const failed = runs.filter((r) => r.status === 'Failed').length;

      const view: ReportsAnalyticsView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-D13-21',
            label: 'Scheduled Reports',
            value: scheduled.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-22',
            label: 'Editions Past Their Freshness Promise',
            value: scheduled.filter((report) => report.isStale).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-23',
            label: 'Runs Failed Or Partial (30 days)',
            value: failed + partial,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-24',
            label: 'Schedules Paused',
            value: scheduled.filter((report) => report.pausedReason !== null).length,
            unit: 'count',
            confidence: 'High',
          }),
        ],
        scheduled,
        onDemand,
        recentRuns: runs,
        runOutcomeTrend: chartPayload({
          chartId: 'CHT-D13-RUNS',
          title: 'Run outcomes by week',
          accessibleSummary:
            'Completed, partial and failed scheduled runs for each of the last six weeks. Failures cluster in the two weeks the Finance ERP extract was unavailable.',
          xLabel: 'Week commencing',
          series: [
            {
              seriesId: 'succeeded',
              label: 'Completed',
              points: [
                { x: shiftDays(asOf, -35), y: 11 },
                { x: shiftDays(asOf, -28), y: 12 },
                { x: shiftDays(asOf, -21), y: 10 },
                { x: shiftDays(asOf, -14), y: 9 },
                { x: shiftDays(asOf, -7), y: 8 },
                { x: shiftDays(asOf, 0), y: succeeded },
              ],
            },
            {
              seriesId: 'partial',
              label: 'Partial data',
              points: [
                { x: shiftDays(asOf, -35), y: 0 },
                { x: shiftDays(asOf, -28), y: 1 },
                { x: shiftDays(asOf, -21), y: 0 },
                { x: shiftDays(asOf, -14), y: 2 },
                { x: shiftDays(asOf, -7), y: 2 },
                { x: shiftDays(asOf, 0), y: partial },
              ],
            },
            {
              seriesId: 'failed',
              label: 'Failed',
              points: [
                { x: shiftDays(asOf, -35), y: 0 },
                { x: shiftDays(asOf, -28), y: 0 },
                { x: shiftDays(asOf, -21), y: 1 },
                { x: shiftDays(asOf, -14), y: 2 },
                { x: shiftDays(asOf, -7), y: 1 },
                { x: shiftDays(asOf, 0), y: failed },
              ],
            },
          ],
          refreshedAt: momentBefore(asOf, 0, '06:30'),
        }),
        deliveryMix: chartPayload({
          chartId: 'CHT-D13-DELIVERY',
          title: 'Recipients by delivery channel',
          accessibleSummary:
            'How many people receive scheduled output on each channel. Email carries the great majority; the finance drop is a single shared destination.',
          xLabel: 'Channel',
          series: [
            {
              seriesId: 'recipients',
              label: 'Recipients',
              points: [
                { x: 'Email', y: 151 },
                { x: 'In-app', y: 96 },
                { x: 'Finance drop', y: 1 },
                { x: 'API', y: null },
              ],
            },
          ],
          refreshedAt: momentBefore(asOf, 0, '06:30'),
        }),
        executionUnavailableReason:
          'There is no reporting engine connected in this build. "Prepare" opens the definition with your parameters filled in; it does not queue a run, generate a file or send anything to anyone.',
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminCommandCenter,
          sources: [...sources, SOURCE_SYSTEMS.financeErp],
          confidence: 'Medium',
          actions: [
            {
              action: 'reports.run',
              permission: 'create',
              enabled: false,
              disabledReason:
                'No reporting engine is connected in this build, so a run cannot be queued.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },

    async getTaxonomyGovernance(
      filters: GlobalFilters,
    ): Promise<QueryResult<TaxonomyGovernanceView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const vocabularies = buildVocabularies();
      const versions = buildVersions();
      const changes = buildChanges();
      const policies = buildPolicies();

      const view: TaxonomyGovernanceView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-D13-31',
            label: 'Vocabularies In Force',
            value: vocabularies.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-32',
            label: 'Versions Awaiting Approval',
            value: versions.filter(
              (version) => version.status === 'InReview' || version.status === 'Draft',
            ).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-33',
            label: 'Changes Without A Migration Rule',
            value: changes.filter((change) => change.migrationRule === null).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-34',
            label: 'Records Affected By Pending Changes',
            value: changes
              .filter(
                (change) => change.toVersion !== '2026.2' || change.vocabularyCode !== 'VOC-BEN',
              )
              .reduce((sum, change) => sum + change.affectedRecordCount, 0),
            unit: 'count',
            confidence: 'Medium',
          }),
          metricValue({
            dataset,
            metricId: 'MET-D13-35',
            label: 'Policy Reviews Overdue',
            value: policies.filter((policy) => policy.overdueReviewReason !== null).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
        ],
        vocabularies,
        versions,
        changes,
        policies,
        approvalRoute: ROUTES.adminCommandCenter,
        inForceAsOf: asOf,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminCommandCenter,
          sources,
          confidence: 'High',
          actions: [
            {
              action: 'taxonomy.publish',
              permission: 'approve',
              enabled: false,
              disabledReason:
                'Publishing a version is an administrative command and the governance workflow is not connected in this build.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },
  };
}
